import { IMAGES, ELEMENTS } from "@/lib/brand";
import { BrandSignature } from "@/components/BrandSignature";

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <img src={IMAGES.himalayas} alt="The Himalayas" className="absolute inset-0 w-full h-full object-cover kc-hero-image" />
        <div className="absolute inset-0 hero-scrim-strong" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 text-[#F8F6F2]">
          <div className="overline text-[#D4AF37] kc-fade-up">Our House</div>
          <h1 className="h-display text-[44px] sm:text-[56px] md:text-[64px] lg:text-[80px] font-light leading-[1.05] mt-6 max-w-5xl kc-fade-up kc-delay-1 tracking-[-0.025em]" style={{textShadow:"0 2px 32px rgba(0,0,0,0.35)"}}>
            Luxury fades. <span className="italic text-[#D4AF37]">Nature</span> remains. Divinity stays forever.
          </h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="overline">Brand Purpose</div>
            <div className="index-num mt-8">— Since Sersi</div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p className="font-serif-display text-3xl md:text-5xl font-light leading-[1.15] text-ink tracking-[-0.02em]">
              THE KC INDIA is a divine hospitality house where the serenity of the Himalayas meets modern comfort and timeless Indian hospitality.
            </p>
            <p className="text-ink-soft mt-10 text-[15.5px] leading-[1.85] max-w-2xl">
              We do not simply offer accommodation. We create experiences that reconnect people with Nature, Peace, and Divinity — one arrival, one meal, one sunrise at a time.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-cream section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
          <div>
            <div className="overline">Vision</div>
            <p className="font-serif-display text-3xl md:text-4xl mt-6 leading-[1.2] font-light tracking-[-0.02em]">
              To become India's most trusted divine hospitality house, inspiring meaningful journeys through the Himalayas.
            </p>
          </div>
          <div>
            <div className="overline">Mission</div>
            <p className="font-serif-display text-3xl md:text-4xl mt-6 leading-[1.2] font-light tracking-[-0.02em]">
              To deliver seamless hospitality, authentic Himalayan experiences and thoughtful modern comforts.
            </p>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="overline">Our Values</div>
          <h2 className="font-serif-display text-5xl md:text-6xl mt-6 font-light leading-[1] max-w-2xl tracking-[-0.02em]">Five ideas we return to, always.</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-ink/10 mt-16">
            {[
              { n: "01", t: "Faith",       d: "Serve with reverence." },
              { n: "02", t: "Simplicity",  d: "Luxury through simplicity." },
              { n: "03", t: "Hospitality", d: "Every guest is family." },
              { n: "04", t: "Trust",       d: "Transparent. Honest. Reliable." },
              { n: "05", t: "Nature",      d: "Respect the Himalayas." },
            ].map(v => (
              <div key={v.t} className="bg-kc p-8 md:p-10">
                <div className="index-num">№ {v.n}</div>
                <div className="font-serif-display text-3xl mt-8">{v.t}</div>
                <p className="text-ink-soft text-[13.5px] mt-4 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0F0F0F] text-[#F8F6F2] section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="overline text-[#D4AF37]">The Five Elements</div>
          <h2 className="font-serif-display text-5xl md:text-6xl mt-6 font-light leading-[1] tracking-[-0.02em]">Pancha <span className="italic text-[#D4AF37]">Mahabhuta</span>.</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#D4AF37]/15 mt-16">
            {ELEMENTS.map((e, i) => (
              <div key={e.name} className="bg-[#0F0F0F] p-8 md:p-10">
                <div className="index-num text-[#D4AF37]">№ 0{i + 1}</div>
                <div className="font-devanagari text-3xl text-[#D4AF37] mt-8">{e.hi}</div>
                <div className="font-serif-display text-3xl mt-2">{e.name}</div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#F8F6F2]/60 mt-3">{e.meaning}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="kc-ornament max-w-md mx-auto">Brand Signature</div>
          <div className="font-serif-display text-5xl md:text-7xl mt-14 font-light tracking-[-0.03em]">
            THE <span className="italic text-gold">KC</span> INDIA
          </div>
          <div className="mt-10 flex justify-center">
            <BrandSignature size="lg" tone="ink" align="center" data-testid="about-brand-signature" />
          </div>
          <div className="mt-10 text-[10px] uppercase tracking-[0.42em] text-gold">Nature · Comfort · Divinity</div>
          <div className="font-devanagari text-2xl text-ink-soft mt-3">प्रकृति · सुविधा · दिव्यता</div>
        </div>
      </section>
    </div>
  );
}
